'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface FamilyMember {
  id: string;
  name: string;
  avatar: string;
  avatarAlt: string;
  role: string;
  lastViewed: string;
}

interface AdherenceData {
  medicationName: string;
  adherenceRate: number;
  missedDoses: number;
  lastTaken: string;
}

interface FamilyVisibilityPanelProps {
  familyMembers: FamilyMember[];
  adherenceData: AdherenceData[];
}

export default function FamilyVisibilityPanel({
  familyMembers,
  adherenceData,
}: FamilyVisibilityPanelProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const getAdherenceColor = (rate: number) => {
    if (rate >= 90) return 'text-success';
    if (rate >= 70) return 'text-warning';
    return 'text-error';
  };

  const getAdherenceBarColor = (rate: number) => {
    if (rate >= 90) return 'bg-success';
    if (rate >= 70) return 'bg-warning';
    return 'bg-error';
  };

  return (
    <div className="glass rounded-lg overflow-hidden">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full p-4 lg:p-6 flex items-center justify-between hover:bg-muted/50 transition-smooth"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <Icon name="UsersIcon" size={24} className="text-primary" />
          </div>
          <div className="text-left">
            <h3 className="text-lg font-heading font-semibold text-foreground">
              Family Visibility
            </h3>
            <p className="text-sm font-caption text-muted-foreground">
              {familyMembers.length} member{familyMembers.length > 1 ? 's' : ''}{' '}
              tracking
            </p>
          </div>
        </div>
        <Icon
          name={isExpanded ? 'ChevronUpIcon' : 'ChevronDownIcon'}
          size={20}
          className="text-muted-foreground"
        />
      </button>

      {isExpanded && (
        <div className="border-t border-sage-200/30 p-4 lg:p-6 space-y-6 animate-slide-in-up">
          <div>
            <h4 className="text-sm font-caption font-medium text-foreground mb-3">
              Family Members
            </h4>
            <div className="space-y-2">
              {familyMembers.map((member) => (
                <div
                  key={member.id}
                  className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted transition-smooth"
                >
                  <div className="w-10 h-10 rounded-full overflow-hidden bg-sage-100 flex-shrink-0">
                    <AppImage
                      src={member.avatar}
                      alt={member.avatarAlt}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-caption font-medium text-foreground">
                      {member.name}
                    </p>
                    <p className="text-xs font-caption text-muted-foreground">
                      {member.role} • Last viewed {member.lastViewed}
                    </p>
                  </div>
                  <Icon
                    name="EyeIcon"
                    size={16}
                    className="text-muted-foreground flex-shrink-0"
                  />
                </div>
              ))}
            </div>
          </div>

          <div>
            <h4 className="text-sm font-caption font-medium text-foreground mb-3">
              Adherence Overview
            </h4>
            <div className="space-y-4">
              {adherenceData.map((data, index) => (
                <div key={index} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-caption font-medium text-foreground">
                      {data.medicationName}
                    </p>
                    <span
                      className={`text-sm font-caption font-semibold ${getAdherenceColor(
                        data.adherenceRate
                      )}`}
                    >
                      {data.adherenceRate}%
                    </span>
                  </div>
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <div
                      className={`h-full ${getAdherenceBarColor(
                        data.adherenceRate
                      )} transition-smooth`}
                      style={{ width: `${data.adherenceRate}%` }}
                    ></div>
                  </div>
                  <div className="flex items-center justify-between text-xs font-caption text-muted-foreground">
                    <span>
                      {data.missedDoses} missed dose
                      {data.missedDoses !== 1 ? 's' : ''} this week
                    </span>
                    <span>Last taken {data.lastTaken}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="p-4 rounded-lg bg-sage-50 dark:bg-sage-900/20 border border-sage-200/30">
            <div className="flex items-start gap-3">
              <Icon
                name="InformationCircleIcon"
                size={20}
                className="text-primary flex-shrink-0 mt-0.5"
              />
              <p className="text-sm font-caption text-muted-foreground">
                Family members can view medication schedules and adherence patterns
                but cannot edit or log doses.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}